/**
 * 63.13 step 2 (the owner, after playing it: he couldn't tell whether the gunship left out of ammo or because its loiter
 * ended): each of the side's gunships in a line for the HUD — on its way in (the time to station), on station (the time
 * left and the load: the side gun's rounds by calibre, the chin's, the rockets), Winchester (the load spent, still circling),
 * and on its way home, with why (its time up, the load spent, or broken off under fire on its way in). The loiter is timed
 * (core/dropship.ts scheduleFlight); an empty load does not send it home. Presentation only: reads state, writes none.
 */
import { PHYS_DT } from '../constants';
import type { DropshipFlight, GameState, GunshipLoad } from '../types';

/** Seconds as m:ss. */
function clock(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Its load spent: no round or rocket left in any weapon. */
export function winchester(load: GunshipLoad): boolean {
  return load.left.chin <= 0 && load.left.side <= 0 && load.left.swarm <= 0 && load.left.heavy <= 0 && load.left.pods <= 0;
}

/** What it has left, in the words the HUD shows (the empty ones left out). */
function loadWords(load: GunshipLoad): string {
  const L = load.left;
  return [[`${load.gun} mm`, L.side], [`${load.chin ?? 30} mm`, L.chin], ['rockets', L.swarm], ['heavy', L.heavy], ['pods', L.pods]]
    .filter(([, n]) => (n as number) > 0).map(([w, n]) => `${w} ${n}`).join(' · ');
}

/** Gunship `f`'s line at `tick` (null before it is called or once gone). */
export function gunshipLine(f: DropshipFlight, tick: number): string | null {
  const load = f.gunship;
  if (!load || tick < f.calledTick || tick >= f.goneTick) return null;
  if (f.evade?.kind === 'break') return 'Gunship: broke off under fire, returning';
  if (tick < f.landTick) return `Gunship: inbound, on station in ${clock((f.landTick - tick) * PHYS_DT)}`;
  const empty = winchester(load);
  if (tick < f.liftTick) return empty ? `Gunship: Winchester, on station ${clock((f.liftTick - tick) * PHYS_DT)}` : `Gunship: on station ${clock((f.liftTick - tick) * PHYS_DT)} · ${loadWords(load)}`;
  return empty ? 'Gunship: Winchester, returning' : 'Gunship: time up, returning';
}

/** The side's gunships' lines at `tick`, in their call order. */
export function gunshipLines(state: Pick<GameState, 'dropships'>, team: number, tick: number): string[] {
  const out: string[] = [];
  for (const f of state.dropships?.flights ?? []) {
    if (f.team !== team) continue;
    const line = gunshipLine(f, tick);
    if (line) out.push(line);
  }
  return out;
}
